import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FiCheck, FiChevronDown, FiClock, FiDownload, FiHash, FiMessageCircle, FiPaperclip, FiPlus, FiRefreshCw, FiSearch, FiSend, FiUsers, FiX } from "react-icons/fi";
import { GlobalState } from "../../data/Context";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import { createChatGroup, createDirectChat, deleteChatMessage, getChatContext, getChatGroupById, listChatGroups, listChatMentionables, listChatMessages, sendChatMessage, updateChatGroupMembers } from "../../api/chat";
import { buildAssetUrl } from "../../data/authStorage";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const EMPTY_GROUP = null;
const MESSAGE_LIMIT = 60;
const GROUP_POLL_MS = 15000;
const MESSAGE_POLL_MS = 5000;

const CHAT_GROUPS_CACHE_KEY = "cehr_chat_groups";
const getChatMessagesCacheKey = (groupId) => `cehr_chat_messages_${groupId}`;

const createTempMessageId = () => `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getInitials = (name) => {
  const parts = `${name || ""}`.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return "??";
  }
  return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("");
};

const formatTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const renderLinkedText = (text) => {
  const parts = `${text || ""}`.split(/(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi);
  return parts.map((part, index) => {
    if (!/^https?:\/\//i.test(part) && !/^www\./i.test(part)) return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
    const href = /^https?:\/\//i.test(part) ? part : `https://${part}`;
    return <a key={`${part}-${index}`} href={href} target="_blank" rel="noreferrer noopener" className="font-semibold underline decoration-current/40 underline-offset-2 hover:decoration-current">{part}</a>;
  });
};

const formatMessageDate = (value) => {
  if (!value) {
    return "Today";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Today";
  }

  const today = new Date();
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();

  if (sameDay) {
    return "Today";
  }

  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(date);
};

const normalizeString = (value) => `${value || ""}`.trim().toLowerCase();

const getEmployeeName = (employee) => {
  if (!employee) {
    return "Unknown member";
  }

  return (
    employee.name ||
    `${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
    employee.officialEmail ||
    employee.personalEmail ||
    "Unknown member"
  );
};

const getRoleLabel = (context) => {
  if (context?.role === "organization") {
    return "Admin";
  }

  if (context?.canCreateGroups) {
    return "Head of department";
  }

  return "Staff";
};

const getSenderLabel = (message) => {
  if (message?.senderEmployee?.name) {
    return message.senderEmployee.name;
  }

  return message?.senderName || "System";
};

const getSenderSubtitle = (message) => {
  const senderEmployee = message?.senderEmployee;
  if (!senderEmployee) {
    return message?.senderType === "organization" ? "Organization" : "Member";
  }

  return [senderEmployee.positionName, senderEmployee.departmentName].filter(Boolean).join(" • ") || "Member";
};

const groupMessagesByDate = (messages) => {
  return messages.reduce((accumulator, message) => {
    const bucket = formatMessageDate(message.createdAt);
    if (!accumulator[bucket]) {
      accumulator[bucket] = [];
    }
    accumulator[bucket].push(message);
    return accumulator;
  }, {});
};

function Avatar({ name, src, size = "md" }) {
  const sizeClasses = {
    sm: "h-8 w-8 text-[10px]",
    md: "h-10 w-10 text-xs",
    lg: "h-12 w-12 text-sm",
    xl: "h-14 w-14 text-base",
  };
  const imageSrc = `${src || ""}`.startsWith("/uploads/") ? buildAssetUrl(src) : src;

  return (
    <div className={`relative flex items-center justify-center overflow-hidden rounded-full bg-slate-900 text-white ${sizeClasses[size]}`}>
      {imageSrc ? (
        <img src={imageSrc} alt={name} className="h-full w-full object-cover" />
      ) : (
        <span className="font-semibold tracking-wide">{getInitials(name)}</span>
      )}
    </div>
  );
}

function ModalShell({ title, description, onClose, children, actions }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center px-0 py-0 sm:items-center sm:px-4 sm:py-6">
      <button type="button" aria-label="Close modal" onClick={onClose} className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" />
      <div className="relative z-10 w-full max-w-3xl overflow-hidden rounded-t-3xl border border-white/10 bg-white shadow-2xl sm:rounded-3xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-4 py-4 sm:px-6 sm:py-5">
          <div>
            <h2 className="text-base font-semibold text-slate-950 sm:text-lg">{title}</h2>
            {description ? <p className="mt-1 text-[11px] text-slate-500 sm:text-sm">{description}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
          >
            <FiX />
          </button>
        </div>
        <div className="max-h-[calc(100vh-9rem)] overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">{children}</div>
        {actions ? <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-4 py-3 sm:px-6 sm:py-4">{actions}</div> : null}
      </div>
    </div>
  );
}

function MemberPill({ employee, selected, onToggle, disabled }) {
  const name = getEmployeeName(employee);
  const meta = [employee?.positionName, employee?.departmentName].filter(Boolean).join(" • ");

  return (
    <button
      type="button"
      onClick={() => onToggle(employee.id)}
      disabled={disabled}
      className={`flex w-full items-center gap-3 rounded-2xl border px-3 py-3 text-left transition ${selected ? "border-cyan-500 bg-cyan-50" : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"} ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
    >
      <Avatar name={name} src={employee?.profilePhotoUrl} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-semibold text-slate-900">{name}</p>
          {selected ? <FiCheck className="shrink-0 text-cyan-600" /> : null}
        </div>
        <p className="truncate text-xs text-slate-500">{meta || employee?.officialEmail || employee?.personalEmail || ""}</p>
      </div>
    </button>
  );
}

export default function ChatPage() {
  const location = useLocation();
  const requestedGroupId = useMemo(
    () => location.state?.requestedGroupId || new URLSearchParams(location.search).get("group") || "",
    [location.search, location.state],
  );
  const { organization } = useContext(GlobalState);
  const { currentEmployee, currentEmployeeName, currentEmployeePhoto, companyAccount } = useCurrentEmployee();

  const [groups, setGroups] = useState([]);
  const [context, setContext] = useState(null);
  const [contextReady, setContextReady] = useState(false);
  const [mentionables, setMentionables] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(EMPTY_GROUP);
  const [activeGroup, setActiveGroup] = useState(null);
  const [messageMenuId, setMessageMenuId] = useState(null);
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [pendingDeleteMessage, setPendingDeleteMessage] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [refreshingGroups, setRefreshingGroups] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [refreshingMessages, setRefreshingMessages] = useState(false);
  const [loadingGroupDetails, setLoadingGroupDetails] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [composerValue, setComposerValue] = useState("");
  const [selectedMentionIds, setSelectedMentionIds] = useState([]);
  const [mentionQuery, setMentionQuery] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [isMobileChat, setIsMobileChat] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", description: "" });
  const [createMemberIds, setCreateMemberIds] = useState([]);
  const [groupMemberIds, setGroupMemberIds] = useState([]);
  const [createError, setCreateError] = useState("");
  const [memberError, setMemberError] = useState("");
  const [savingMembers, setSavingMembers] = useState(false);

  const messageListRef = useRef(null);
  const composerRef = useRef(null);
  const mediaInputRef = useRef(null);
  const activeGroupRef = useRef(activeGroupId);
  const messagesRef = useRef(messages);
  const groupsRef = useRef(groups);
  const activeGroupHintRef = useRef(null);
  const scrollToBottomRef = useRef(false);
  const isMobileChatRef = useRef(false);

  useEffect(() => {
    activeGroupRef.current = activeGroupId;
  }, [activeGroupId]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const syncMobileState = () => {
      isMobileChatRef.current = mediaQuery.matches;
      setIsMobileChat(mediaQuery.matches);
    };

    syncMobileState();
    mediaQuery.addEventListener?.("change", syncMobileState);

    return () => {
      mediaQuery.removeEventListener?.("change", syncMobileState);
    };
  }, []);

  const syncGroupSelection = useCallback((groupId, groupHint = null) => {
    activeGroupHintRef.current = groupHint;

    const nextGroup =
      groupHint ||
      groupsRef.current.find((group) => group.id === groupId) ||
      null;

    setActiveGroup(groupHint || nextGroup);
    setGroupMemberIds((nextGroup?.members || groupHint?.members || []).map((member) => member.employeeId || member.id).filter(Boolean));
    setSelectedMentionIds([]);
    setMentionQuery("");
    setActiveGroupId(groupId);
  }, []);

  const filteredGroups = useMemo(() => {
    const query = normalizeString(search);
    return groups.filter((group) => {
      const matchesFilter =
        filter === "all" ||
        (filter === "department" && group.groupType === "department") ||
        (filter === "custom" && group.groupType !== "department");

      const searchable = normalizeString([group.name, group.description, group.departmentName, group.lastMessagePreview, group.lastMessageSenderName].filter(Boolean).join(" "));
      return matchesFilter && (!query || searchable.includes(query));
    }).sort((left, right) => {
      const unreadDifference = Number(right.unreadCount || 0) - Number(left.unreadCount || 0);
      if (unreadDifference) return unreadDifference;
      return new Date(right.lastMessageAt || right.updatedAt || 0).getTime() - new Date(left.lastMessageAt || left.updatedAt || 0).getTime();
    });
  }, [filter, groups, search]);

  const filteredEmployees = useMemo(() => {
    const query = normalizeString(search);
    const currentEmployeeId = currentEmployee?.id || "";

    return mentionables.filter((employee) => {
      if (!companyAccount && employee.id === currentEmployeeId) {
        return false;
      }

      const searchable = normalizeString([
        getEmployeeName(employee),
        employee?.officialEmail,
        employee?.personalEmail,
        employee?.departmentName,
        employee?.positionName,
      ].filter(Boolean).join(" "));

      return !query || searchable.includes(query);
    });
  }, [companyAccount, currentEmployee?.id, mentionables, search]);

  const canCreateGroups = !!context?.canCreateGroups;
  const currentDepartmentId = context?.currentDepartmentId || null;
  const managedDepartmentId = context?.managedDepartmentId || null;
  const canManageCurrentGroup = Boolean(
    activeGroup?.groupType !== "project" && (
      context?.role === "organization" ||
      (canCreateGroups && (!activeGroup?.departmentId || activeGroup?.departmentId === managedDepartmentId || activeGroup?.departmentId === currentDepartmentId))
    ),
  );

  const currentUserName = companyAccount ? organization?.name || "Organization" : currentEmployeeName;
  const currentUserAvatar = companyAccount ? organization?.logo || organization?.image : currentEmployeePhoto;
  const currentUserSubtitle = getRoleLabel(context);

  const activeMentionCandidates = useMemo(() => {
    const memberIds = new Set((activeGroup?.members || []).map((member) => member.employeeId || member.id));
    const groupMentionables = mentionables.filter((employee) => memberIds.has(employee.id));
    if (!mentionQuery) {
      return groupMentionables.slice(0, 8);
    }

    const query = normalizeString(mentionQuery);
    return groupMentionables
      .filter((employee) => {
        const searchable = normalizeString([getEmployeeName(employee), employee?.officialEmail, employee?.personalEmail, employee?.departmentName, employee?.positionName].filter(Boolean).join(" "));
        return searchable.includes(query);
      })
      .slice(0, 8);
  }, [activeGroup?.members, mentionQuery, mentionables]);

  const selectedMentionLookup = useMemo(() => {
    const lookup = new Map();
    mentionables.forEach((employee) => {
      lookup.set(employee.id, employee);
    });
    return lookup;
  }, [mentionables]);

  const loadContext = async () => {
    try {
      const response = await getChatContext();
      setContext(response || null);
    } catch (requestError) {
      setContext(null);
      setError(requestError?.response?.data?.message || "Unable to load chat permissions.");
    } finally {
      setContextReady(true);
    }
  };

  const loadMentionables = async () => {
    try {
      const response = await listChatMentionables();
      setMentionables(Array.isArray(response?.employees) ? response.employees : []);
    } catch (requestError) {
      setMentionables([]);
      setError((previous) => previous || requestError?.response?.data?.message || "Unable to load chat members.");
    }
  };

  const loadMessages = useCallback(async (groupId, { reset = false, before, silent = false, scrollToBottom = false } = {}) => {
    if (!groupId) {
      return;
    }

    try {
      const cachedMessages = !before ? readSessionCache(getChatMessagesCacheKey(groupId), []) : [];
      const hasSelectedGroupMessages =
        activeGroupRef.current === groupId &&
        messagesRef.current.some((message) => !message.groupId || message.groupId === groupId);
      if (reset && cachedMessages.length && !hasSelectedGroupMessages) {
        setMessages(cachedMessages);
        setHasMoreMessages(false);
        if (scrollToBottom) {
          scrollToBottomRef.current = true;
        }
        silent = true;
      }

      if (!before && !silent) {
        setLoadingMessages(true);
      } else if (!before && silent) {
        setRefreshingMessages(true);
      } else {
        setLoadingOlder(true);
      }

      const response = await listChatMessages(groupId, {
        limit: MESSAGE_LIMIT,
        ...(before ? { before } : {}),
      });

      const nextMessages = Array.isArray(response?.messages) ? response.messages : [];
      setGroups((currentGroups) => currentGroups.map((group) => (
        group.id === groupId ? { ...group, unreadCount: 0 } : group
      )));
      setHasMoreMessages(Boolean(response?.pagination?.hasMore));
      if (!before) {
        writeSessionCache(getChatMessagesCacheKey(groupId), nextMessages);
      }
      if (scrollToBottom) {
        scrollToBottomRef.current = true;
      }
      setMessages((currentMessages) => {
        if (reset) {
          const pendingMessages = currentMessages.filter(
            (message) =>
              message.isOptimistic &&
              message.groupId === groupId &&
              !nextMessages.some((item) => item.id === message.id),
          );
          return [...nextMessages, ...pendingMessages];
        }

        if (before) {
          return [...nextMessages, ...currentMessages.filter((message) => !nextMessages.some((item) => item.id === message.id))];
        }

        return nextMessages.length ? nextMessages : currentMessages;
      });
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Unable to load chat messages.");
    } finally {
      setLoadingMessages(false);
      setRefreshingMessages(false);
      setLoadingOlder(false);
    }
  }, []);

  const loadGroups = useCallback(async (preserveSelection = true, options = {}) => {
    const { silent = false } = options;

    try {
      const cachedGroups = readSessionCache(CHAT_GROUPS_CACHE_KEY, []);
      if (!groupsRef.current.length && cachedGroups.length) {
        setGroups(cachedGroups);
        setLoadingGroups(false);
        if (!activeGroupRef.current && !isMobileChatRef.current) {
          const cachedRequestedGroup = requestedGroupId && cachedGroups.find((group) => group.id === requestedGroupId);
          setActiveGroupId(cachedRequestedGroup?.id || cachedGroups[0].id);
        }
      }

      if (silent || cachedGroups.length) {
        setRefreshingGroups(true);
      } else {
        setLoadingGroups(true);
      }

      const response = await listChatGroups();
      const nextGroups = Array.isArray(response?.groups) ? response.groups : [];
      writeSessionCache(CHAT_GROUPS_CACHE_KEY, nextGroups);
      setGroups(nextGroups);

      if (!nextGroups.length) {
        setActiveGroupId(null);
        setActiveGroup(null);
        setMessages([]);
        setHasMoreMessages(false);
        return;
      }

      const requestedExists = requestedGroupId && nextGroups.some((group) => group.id === requestedGroupId);
      const selectedExists = preserveSelection && activeGroupRef.current && nextGroups.some((group) => group.id === activeGroupRef.current);
      const nextActiveGroupId = requestedExists
        ? requestedGroupId
        : selectedExists
        ? activeGroupRef.current
        : isMobileChatRef.current
          ? null
          : nextGroups[0].id;
      if (nextActiveGroupId !== activeGroupRef.current) {
        setActiveGroupId(nextActiveGroupId);
      }
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Unable to load chat groups.");
    } finally {
      setLoadingGroups(false);
      setRefreshingGroups(false);
    }
  }, [requestedGroupId]);

  const loadActiveGroup = useCallback(async (groupId) => {
    if (!groupId) {
      setActiveGroup(null);
      setMessages([]);
      setHasMoreMessages(false);
      return;
    }

    try {
      const group = activeGroupHintRef.current || groupsRef.current.find((item) => item.id === groupId) || null;

      if (group) {
        setActiveGroup(group);
        setGroupMemberIds((group?.members || []).map((member) => member.employeeId || member.id).filter(Boolean));
      }

      setLoadingGroupDetails(true);
      const hasCurrentGroupMessages = Array.isArray(messagesRef.current) && messagesRef.current.length > 0 && activeGroupRef.current === groupId;
      const [detailResponse] = await Promise.all([
        getChatGroupById(groupId),
        loadMessages(groupId, { reset: true, silent: hasCurrentGroupMessages, scrollToBottom: true }),
      ]);
      if (detailResponse?.group) {
        setActiveGroup(detailResponse.group);
        setGroupMemberIds((detailResponse.group.members || []).map((member) => member.employeeId || member.id).filter(Boolean));
      }
    } catch (requestError) {
      const message = requestError?.response?.data?.message || "Unable to load the selected conversation.";
      // A stale deep-link (for example, a deleted project chat) should not leave
      // the whole chat screen in an error state. Return to the conversation list.
      if (/chat group not found|conversation not found/i.test(message)) {
        setActiveGroupId("");
        setError("");
      } else {
        setError(message);
      }
      if (!activeGroupHintRef.current) {
        setActiveGroup(null);
        setMessages([]);
      }
    } finally {
      setLoadingGroupDetails(false);
    }
  }, [loadMessages]);

  useEffect(() => {
    setError("");
    loadContext();
    loadMentionables();
  }, []);

  useEffect(() => {
    if (contextReady) {
      loadGroups(false);
    }
  }, [contextReady, loadGroups]);

  useEffect(() => {
    if (!groups.length) {
      return;
    }

    if (isMobileChat && !activeGroupId) {
      return;
    }

    const selected = requestedGroupId && groups.some((group) => group.id === requestedGroupId)
      ? requestedGroupId
      : groups.some((group) => group.id === activeGroupId)
      ? activeGroupId
      : isMobileChat
        ? null
        : groups[0]?.id || null;
    if (selected !== activeGroupId) {
      setActiveGroupId(selected);
    }
  }, [activeGroupId, groups, isMobileChat, requestedGroupId]);

  useEffect(() => {
    loadActiveGroup(activeGroupId);
  }, [activeGroupId, loadActiveGroup]);

  useEffect(() => {
    const groupTimer = window.setInterval(() => {
      loadGroups(true, { silent: true });
    }, GROUP_POLL_MS);

    return () => window.clearInterval(groupTimer);
  }, [context?.role, context?.canCreateGroups, currentDepartmentId, loadGroups]);

  useEffect(() => {
    if (!activeGroupId) {
      return undefined;
    }

    const messageTimer = window.setInterval(() => {
      loadMessages(activeGroupId, { reset: true, silent: true });
    }, MESSAGE_POLL_MS);

    return () => window.clearInterval(messageTimer);
  }, [activeGroupId, loadMessages]);

  useEffect(() => {
    if (!scrollToBottomRef.current || !messageListRef.current) {
      return;
    }

    messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    scrollToBottomRef.current = false;
  }, [messages, activeGroupId]);

  const handleGroupSelection = (groupId) => {
    syncGroupSelection(groupId);
  };

  const openDirectConversation = async (employeeId) => {
    if (!employeeId) {
      return;
    }

    try {
      const response = await createDirectChat({ employeeId });
      const directGroupId = response?.group?.id;

      if (response?.group) {
        setGroups((currentGroups) => {
          const nextGroups = currentGroups.filter((group) => group.id !== response.group.id);
          return [response.group, ...nextGroups];
        });
      }

      if (directGroupId) {
        setShowMembersModal(false);
        syncGroupSelection(directGroupId, response?.group || null);
      }

      loadGroups(true, { silent: true });
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Unable to open direct conversation.");
    }
  };

  const updateComposerMentions = (text) => {
    const selectionEnd = composerRef.current?.selectionStart ?? text.length;
    const beforeCursor = text.slice(0, selectionEnd);
    const mentionMatch = beforeCursor.match(/(^|\s)@([\w.-]{1,40})$/);

    if (mentionMatch) {
      setMentionQuery(mentionMatch[2]);
      return;
    }

    setMentionQuery("");
  };

  const handleComposerChange = (event) => {
    const nextValue = event.target.value;
    setComposerValue(nextValue);
    updateComposerMentions(nextValue);
  };

  const insertMention = (employee) => {
    const textarea = composerRef.current;
    if (!textarea) {
      return;
    }

    const selectionStart = textarea.selectionStart ?? composerValue.length;
    const selectionEnd = textarea.selectionEnd ?? composerValue.length;
    const beforeCursor = composerValue.slice(0, selectionStart);
    const afterCursor = composerValue.slice(selectionEnd);
    const mentionMatch = beforeCursor.match(/(^|\s)@([\w.-]{1,40})$/);

    if (!mentionMatch) {
      return;
    }

    const prefix = beforeCursor.slice(0, beforeCursor.length - mentionMatch[2].length - 1);
    const replacement = `${mentionMatch[1]}@${getEmployeeName(employee)} `;
    const nextValue = `${prefix}${replacement}${afterCursor}`;

    setComposerValue(nextValue);
    setSelectedMentionIds((currentIds) => Array.from(new Set([...currentIds, employee.id])));
    setMentionQuery("");

    window.requestAnimationFrame(() => {
      const nextCaret = prefix.length + replacement.length;
      textarea.focus();
      textarea.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const removeSelectedMention = (employeeId) => {
    setSelectedMentionIds((currentIds) => currentIds.filter((value) => value !== employeeId));
  };

  const selectMedia = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    const allowed = files.filter((file) => {
      const isSupportedMedia = /^(image|video|audio)\//i.test(file.type);
      const isSupportedDocument = /^(application\/pdf|application\/msword|application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document)$/i.test(file.type)
        || /\.(pdf|doc|docx)$/i.test(file.name);
      return (isSupportedMedia || isSupportedDocument) && file.size <= 20 * 1024 * 1024;
    });
    if (allowed.length !== files.length) {
      const oversized = files.find((file) => file.size > 20 * 1024 * 1024);
      setError(oversized ? `${oversized.name} is too large (${(oversized.size / (1024 * 1024)).toFixed(1)} MB). Chat files must be 20 MB or smaller.` : "Only images, videos, audio, PDF, or Word files up to 20 MB can be shared.");
    }
    setSelectedMedia((current) => [...current, ...allowed].slice(0, 5));
  };

  const sendMessage = async (event) => {
    event.preventDefault();

    const body = composerValue.trim();
    const groupId = activeGroupId;
    const mentions = [...selectedMentionIds];

    if (!groupId || (!body && selectedMedia.length === 0)) {
      return;
    }

    const tempId = createTempMessageId();
    const createdAt = new Date().toISOString();
    const optimisticMessage = {
      id: tempId,
      groupId,
      senderType: companyAccount ? "organization" : "staff",
      senderEmployeeId: companyAccount ? null : currentEmployee?.id || null,
      senderName: currentUserName,
      senderEmployee: companyAccount
        ? null
        : {
            ...currentEmployee,
            name: currentUserName,
            profilePhotoUrl: currentEmployeePhoto,
          },
      body,
      attachments: selectedMedia.map((file) => ({ url: URL.createObjectURL(file), name: file.name, mimeType: file.type, size: file.size })),
      mentionedEmployeeIds: mentions,
      createdAt,
      isOptimistic: true,
    };

    setComposerValue("");
    const mediaToSend = [...selectedMedia];
    setSelectedMedia([]);
    setSelectedMentionIds([]);
    setMentionQuery("");
    scrollToBottomRef.current = true;
    setMessages((currentMessages) => [...currentMessages, optimisticMessage]);
    setGroups((currentGroups) =>
      currentGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              latestMessage: optimisticMessage,
              lastMessagePreview: body.slice(0, 160) || `Shared ${mediaToSend.length} media ${mediaToSend.length === 1 ? "item" : "items"}`,
              lastMessageAt: createdAt,
              lastMessageSenderName: currentUserName,
              messageCount: Number(group.messageCount || 0) + 1,
            }
          : group,
      ),
    );

    try {
      setSendingMessage(true);
      const response = await sendChatMessage(groupId, {
        body,
        mentionedEmployeeIds: mentions,
        files: mediaToSend,
      });
      const savedMessage = response?.chatMessage || null;

      if (savedMessage) {
        setMessages((currentMessages) =>
          currentMessages.map((message) => (message.id === tempId ? savedMessage : message)),
        );
        setGroups((currentGroups) =>
          currentGroups.map((group) =>
            group.id === groupId
              ? {
                  ...group,
                  latestMessage: savedMessage,
                  lastMessagePreview: savedMessage.body?.slice(0, 160) || body.slice(0, 160),
                  lastMessageAt: savedMessage.createdAt || createdAt,
                  lastMessageSenderName: savedMessage.senderName || currentUserName,
                }
              : group,
          ),
        );
      }
    } catch (requestError) {
      setMessages((currentMessages) =>
        currentMessages.map((message) =>
          message.id === tempId ? { ...message, isOptimistic: false, failed: true } : message,
        ),
      );
      setError(requestError?.response?.data?.message || "Unable to send your message.");
    } finally {
      setSendingMessage(false);
      composerRef.current?.focus();
    }
  };

  const loadOlderMessages = async () => {
    if (!activeGroupId || !messages.length) {
      return;
    }

    await loadMessages(activeGroupId, { before: messages[0]?.createdAt });
  };

  const returnToChatList = () => {
    setActiveGroupId(EMPTY_GROUP);
    setActiveGroup(null);
    setMessages([]);
    setSelectedMentionIds([]);
    setMentionQuery("");
  };

  const openCreateModal = () => {
    setCreateError("");
    setCreateForm({ name: "", description: "" });
    setCreateMemberIds([]);
    setShowCreateModal(true);
  };

  const toggleCreateMember = (employeeId) => {
    setCreateMemberIds((currentIds) =>
      currentIds.includes(employeeId)
        ? currentIds.filter((value) => value !== employeeId)
        : [...currentIds, employeeId],
    );
  };

  const submitCreateGroup = async (event) => {
    event.preventDefault();
    setCreateError("");

    try {
      const response = await createChatGroup({
        name: createForm.name,
        description: createForm.description,
        memberIds: createMemberIds,
      });
      setShowCreateModal(false);
      await loadGroups(false, { silent: true });
      if (response?.group?.id) {
        setActiveGroupId(response.group.id);
      }
    } catch (requestError) {
      setCreateError(requestError?.response?.data?.message || "Unable to create chat group.");
    }
  };

  const openMembersModal = () => {
    if (!activeGroup) {
      return;
    }

    setGroupMemberIds((activeGroup.members || []).map((member) => member.employeeId || member.id).filter(Boolean));
    setMemberError("");
    setShowMembersModal(true);
  };

  const toggleGroupMember = (employeeId) => {
    setGroupMemberIds((currentIds) =>
      currentIds.includes(employeeId)
        ? currentIds.filter((value) => value !== employeeId)
        : [...currentIds, employeeId],
    );
  };

  const submitMemberUpdate = async (event) => {
    event.preventDefault();
    if (!activeGroupId) {
      return;
    }

    try {
      setSavingMembers(true);
      setMemberError("");
      await updateChatGroupMembers(activeGroupId, { memberIds: groupMemberIds });
      setShowMembersModal(false);
      await loadActiveGroup(activeGroupId);
      await loadGroups(true, { silent: true });
    } catch (requestError) {
      setMemberError(requestError?.response?.data?.message || "Unable to update group members.");
    } finally {
      setSavingMembers(false);
    }
  };

  const downloadAndPreviewPdf = async (event, attachment) => {
    event.preventDefault();
    const previewWindow = window.open("", "_blank");
    if (previewWindow) {
      previewWindow.document.title = attachment.name || "PDF preview";
      previewWindow.document.body.textContent = "Preparing PDF preview...";
    }

    try {
      const response = await fetch(attachment.url);
      if (!response.ok) throw new Error("Unable to fetch attachment");
      const sourceBlob = await response.blob();
      const pdfBlob = sourceBlob.type === "application/pdf"
        ? sourceBlob
        : new Blob([sourceBlob], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(pdfBlob);
      if (previewWindow) previewWindow.location.replace(objectUrl);
      else window.open(objectUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 5 * 60 * 1000);
    } catch {
      if (previewWindow) previewWindow.location.replace(attachment.url);
      else window.open(attachment.url, "_blank", "noopener,noreferrer");
      setError("The PDF was opened directly because an automatic download could not be prepared.");
    }
  };

  const openDocumentPreview = (event, attachment) => {
    event.preventDefault();
    // Office's online viewer cannot read authenticated/private Cloudinary URLs.
    // Open the original asset instead so the user gets the file rather than a
    // misleading "File not found" page from Office.
    window.open(attachment.url, "_blank", "noopener,noreferrer");
  };

  const renderMessage = (message) => {
    const isSelf = Boolean(
      (companyAccount && message.senderType === "organization") ||
      (!companyAccount && message.senderEmployeeId && currentEmployee?.id === message.senderEmployeeId),
    );

    return (
      <div key={message.id} className={`group relative flex ${isSelf ? "justify-end" : "justify-start"}`}>
        {(isSelf || companyAccount) && !message.isOptimistic && !message.deletedAt ? <div className={`absolute top-1 z-10 ${isSelf ? "right-1" : "left-1"}`}><button type="button" onClick={() => setMessageMenuId((current) => current === message.id ? null : message.id)} className="p-1.5 text-slate-300 transition hover:text-white" aria-label="Message options"><FiChevronDown /></button>{messageMenuId === message.id ? <div className="absolute right-0 z-20 mt-1 w-28 rounded-lg border border-slate-200 bg-white p-1 text-left shadow-lg"><button type="button" disabled={deletingMessageId === message.id} onClick={() => { setPendingDeleteMessage(message); setMessageMenuId(null); }} className="w-full rounded-md px-2 py-1.5 text-left text-xs font-semibold text-red-600 hover:bg-red-50">Delete</button></div> : null}</div> : null}
        <div className={`max-w-2xl ${isSelf ? "order-2" : "order-1"}`}>
          {!isSelf ? (
            <div className="mb-2 flex items-center gap-2">
              <Avatar name={getSenderLabel(message)} src={message.senderEmployee?.profilePhotoUrl} size="sm" />
              <div>
                <p className="text-sm font-semibold text-slate-900">{getSenderLabel(message)}</p>
                <p className="text-xs text-slate-500">{getSenderSubtitle(message)}</p>
              </div>
            </div>
          ) : null}

          <div className={`rounded-3xl px-4 py-3 shadow-sm ${isSelf ? "bg-gradient-to-r from-slate-900 to-cyan-900 text-white" : "border border-slate-200 bg-white text-slate-900"}`}>
            {Array.isArray(message.attachments) && message.attachments.length ? <div className="mb-2 grid max-w-[520px] grid-cols-1 gap-2 sm:grid-cols-2">{message.attachments.map((attachment, index) => {
              const type = `${attachment.mimeType || ""}`;
              const isPdf = type.toLowerCase() === "application/pdf" || /\.pdf(?:$|[?#])/i.test(attachment.name || attachment.url || "");
              if (type.startsWith("image/")) return <a key={`${attachment.url}-${index}`} href={attachment.url} target="_blank" rel="noreferrer" className="overflow-hidden rounded-2xl bg-black/10"><img src={attachment.url} alt={attachment.name || "Shared image"} className="max-h-72 w-full object-cover" loading="lazy" /></a>;
              if (type.startsWith("video/")) return <video key={`${attachment.url}-${index}`} src={attachment.url} controls playsInline preload="metadata" className="max-h-72 w-full rounded-2xl bg-black" />;
              if (type.startsWith("audio/")) return <audio key={`${attachment.url}-${index}`} src={attachment.url} controls preload="metadata" className="w-full min-w-[220px]" />;
              if (isPdf) return <button key={`${attachment.url}-${index}`} type="button" onClick={(event) => downloadAndPreviewPdf(event, attachment)} className={`flex min-w-0 items-center gap-3 rounded-2xl border px-3 py-3 text-left text-sm font-semibold transition ${isSelf ? "border-white/20 bg-white/10 text-white hover:bg-white/20" : "border-slate-200 bg-slate-50 text-cyan-800 hover:bg-cyan-50"}`}><FiPaperclip className="shrink-0"/><span className="min-w-0 flex-1 truncate">{attachment.name || "Open PDF"}</span><FiDownload className="shrink-0" aria-hidden="true"/><span className="sr-only">Download and preview PDF</span></button>;
              return <button key={`${attachment.url}-${index}`} type="button" onClick={(event) => openDocumentPreview(event, attachment)} className={`flex min-w-0 items-center gap-3 rounded-2xl border px-3 py-3 text-left text-sm font-semibold transition ${isSelf ? "border-white/20 bg-white/10 text-white hover:bg-white/20" : "border-slate-200 bg-slate-50 text-cyan-800 hover:bg-cyan-50"}`}><FiPaperclip className="shrink-0"/><span className="truncate">{attachment.name || "Open document"}</span><span className="ml-auto text-[10px] font-medium opacity-70">Open</span></button>;
            })}</div> : null}
            {message.body ? <p className="whitespace-pre-wrap break-words text-xs leading-5 sm:text-sm sm:leading-6">{renderLinkedText(message.body)}</p> : null}
            {Array.isArray(message.mentionedEmployeeIds) && message.mentionedEmployeeIds.length > 0 ? (
              <div className={`mt-3 flex flex-wrap gap-2 text-xs ${isSelf ? "text-cyan-100" : "text-cyan-700"}`}>
                {message.mentionedEmployeeIds.map((employeeId) => {
                  const mentionedEmployee = selectedMentionLookup.get(employeeId);
                  return (
                    <span key={employeeId} className={`rounded-full px-3 py-1 ${isSelf ? "bg-white/10" : "bg-cyan-50"}`}>
                      @{getEmployeeName(mentionedEmployee)}
                    </span>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className={`mt-2 flex items-center gap-2 text-xs text-slate-500 ${isSelf ? "justify-end" : "justify-start"}`}>
            {message.failed ? (
              <button type="button" className="inline-flex items-center gap-1 font-semibold text-red-600 hover:text-red-700" onClick={() => { setComposerValue(message.body || ""); setSelectedMentionIds(message.mentionedEmployeeIds || []); setMessages((current) => current.filter((item) => item.id !== message.id)); composerRef.current?.focus(); }} aria-label="Retry failed message">
                <FiX className="shrink-0" /> Retry
              </button>
            ) : message.isOptimistic ? (
              <FiClock className="shrink-0" aria-label="Sending" />
            ) : (
              <FiCheck className="shrink-0 text-emerald-600" aria-label="Sent" />
            )}
            <span>
              {message.failed ? "Not sent" : message.isOptimistic ? "Sending..." : formatTime(message.createdAt)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const groupedMessages = useMemo(() => groupMessagesByDate(messages), [messages]);

  return (
    <div className="h-[calc(100dvh-5rem)] min-h-0 overflow-hidden overscroll-none bg-white text-slate-900 md:h-[calc(100dvh-7.5rem)] md:bg-[radial-gradient(circle_at_top,_rgba(8,145,178,0.16),_transparent_35%),linear-gradient(180deg,_#f8fafc_0%,_#edf5f7_100%)]">
      <div className="mx-auto flex h-full max-w-[1600px] flex-col gap-2 px-0 py-0 md:gap-3 md:px-4 md:py-3 lg:px-6 lg:py-4">
        <div className={`${activeGroup ? "hidden lg:hidden" : "block"} overflow-hidden border-b border-slate-200 bg-white px-3 py-3 md:rounded-[24px] md:border md:border-white/70 md:bg-white/85 md:px-0 md:py-0 md:shadow-[0_20px_60px_rgba(15,23,42,0.08)] md:backdrop-blur-xl`}>
          <div className="flex flex-col gap-2 border-b border-slate-200 px-4 py-2 lg:flex-row lg:items-center lg:justify-between lg:px-5 lg:py-2">
            <div className="hidden max-w-3xl md:block">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-800">
                <FiMessageCircle />
                Team chat
              </div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-950 lg:text-2xl">Live group chat for every team, department, and admin flow.</h1>
            </div>
            <div className="md:hidden">
              <h1 className="text-lg font-semibold text-slate-950">Messages</h1>
              <p className="text-xs text-slate-500">Chat with staff and teams.</p>
            </div>

            <div className="hidden items-center gap-3 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 lg:flex">
              <div className="flex items-center gap-3">
                <Avatar name={currentUserName} src={currentUserAvatar} size="sm" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900">{currentUserName}</p>
                  <p className="text-[11px] text-slate-500">{currentUserSubtitle}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 px-4 py-2.5 lg:flex-row lg:items-center lg:justify-between lg:px-5">
            <div className="flex flex-wrap items-center gap-2">
              {[
                { id: "all", label: "All" },
                { id: "department", label: "Departments" },
                { id: "custom", label: "Custom" },
              ].map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setFilter(item.id)}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-200 ${filter === item.id ? "bg-slate-900 text-white shadow-lg shadow-slate-900/20" : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:shadow-md hover:shadow-slate-300/50 hover:scale-105"}`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <div className="relative">
                <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search conversations"
                className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-11 pr-4 text-sm outline-none transition placeholder:text-slate-400 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100 md:rounded-full lg:w-[300px]"
              />
            </div>

              {canCreateGroups ? (
                <button
                  type="button"
                  onClick={openCreateModal}
                  className="hidden items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-600 to-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-cyan-900/20 transition hover:translate-y-[-1px] md:inline-flex"
                >
                  <FiPlus />
                  New group
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {error ? (
          <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm">
            {error}
          </div>
        ) : null}

        <div className="grid flex-1 min-h-0 gap-0 overflow-hidden lg:gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className={`${activeGroup ? "hidden lg:flex" : "flex"} min-h-0 flex-col overflow-hidden bg-white md:rounded-[28px] md:border md:border-white/70 md:bg-white/90 md:shadow-[0_24px_80px_rgba(15,23,42,0.10)] md:backdrop-blur-xl`}>
            <div className="border-b border-slate-200 px-3 py-3">
              <div className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1" aria-label="Conversation type">
                {[
                  { id: "all", label: "Employees" },
                  { id: "department", label: "Department" },
                  { id: "custom", label: "Custom" },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setFilter(item.id)}
                    className={`min-w-0 truncate rounded-lg px-1.5 py-2 text-[11px] font-semibold transition ${filter === item.id ? "bg-white text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="relative mt-2">
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search chats" className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-xs outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100" />
              </div>
            </div>

            <div className="border-b border-slate-100 px-4 py-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Conversations</p>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-2.5">
              {loadingGroups ? (
                <div className="space-y-3 p-2">
                  {[0, 1, 2, 3].map((item) => (
                    <div key={item} className="animate-pulse rounded-3xl border border-slate-200 p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-full bg-slate-200" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 w-3/5 rounded bg-slate-200" />
                          <div className="h-3 w-4/5 rounded bg-slate-200" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filter === "all" ? (
                filteredGroups.length || filteredEmployees.length ? (
                  <div className="space-y-4">
                    {filteredGroups.length ? (
                      <div className="space-y-2">
                        <p className="px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Conversations</p>
                        {filteredGroups.map((group) => {
                          const unreadCount = Number(group.unreadCount || 0);
                          const latestMessage = group.latestMessage || null;
                          return (
                            <button
                              key={group.id}
                              type="button"
                              onClick={() => handleGroupSelection(group.id)}
                              className={`w-full rounded-2xl border p-3 text-left transition-all ${unreadCount > 0 ? "border-cyan-200 bg-cyan-50/80 shadow-sm" : "border-transparent bg-slate-50 hover:border-slate-200 hover:bg-white"}`}
                            >
                              <div className="flex items-center gap-3">
                                <Avatar name={group.name} src={group.avatar} size="md" />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <h3 className={`truncate text-sm text-slate-900 ${unreadCount > 0 ? "font-bold" : "font-semibold"}`}>{group.name}</h3>
                                    <span className={`shrink-0 text-[11px] ${unreadCount > 0 ? "font-bold text-cyan-800" : "text-slate-500"}`}>{formatTime(group.lastMessageAt) || "New"}</span>
                                  </div>
                                  <div className="mt-1 flex items-center gap-2">
                                    <p className={`min-w-0 flex-1 truncate text-xs ${unreadCount > 0 ? "font-semibold text-slate-700" : "text-slate-500"}`}>
                                      {latestMessage?.body || group.lastMessagePreview || "No messages yet. Start the conversation."}
                                    </p>
                                    {unreadCount > 0 ? (
                                      <span className="inline-flex min-h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-cyan-700 px-1.5 text-[10px] font-bold text-white" aria-label={`${unreadCount} unread messages`}>
                                        {unreadCount > 99 ? "99+" : unreadCount}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                    {filteredEmployees.length ? <div className="space-y-2">
                      <p className="px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Start a conversation</p>
                    {filteredEmployees.map((employee) => {
                      const employeeName = getEmployeeName(employee);
                      const employeeMeta = [employee?.positionName, employee?.departmentName].filter(Boolean).join(" • ");

                      return (
                        <button
                          key={employee.id}
                          type="button"
                          onClick={() => openDirectConversation(employee.id)}
                        className="group w-full rounded-2xl border border-transparent bg-slate-50 p-3 text-left transition-all duration-200 hover:border-slate-200 hover:bg-white hover:shadow-md md:rounded-3xl md:p-4 md:hover:scale-105"
                        >
                          <div className="flex items-center gap-3">
                            <Avatar name={employeeName} src={employee.profilePhotoUrl} size="md" />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold text-slate-900">{employeeName}</p>
                              <p className="truncate text-xs text-slate-500">{employeeMeta || employee?.officialEmail || employee?.personalEmail || ""}</p>
                            </div>
                            <span className="text-xs font-semibold text-cyan-700">Message</span>
                          </div>
                        </button>
                      );
                    })}
                    </div> : null}
                  </div>
                ) : (
                  <div className="flex h-full flex-col items-center justify-center px-6 py-16 text-center">
                    <FiUsers className="text-3xl text-slate-300" />
                    <h3 className="mt-4 text-lg font-semibold text-slate-900">No employees found</h3>
                    <p className="mt-2 max-w-sm text-sm text-slate-500">Try a different search term.</p>
                  </div>
                )
              ) : filteredGroups.length ? (
                <div className="space-y-2">
                  {filteredGroups.map((group) => {
                    const isActive = group.id === activeGroupId;
                    const unreadCount = Number(group.unreadCount || 0);
                    const latestMessage = group.latestMessage || null;
                    const memberPreview = (group.members || []).slice(0, 3).map((member) => member.employee?.name || getEmployeeName(member.employee));

                      return (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => handleGroupSelection(group.id)}
                        className={`group w-full rounded-2xl border p-3 text-left transition-all duration-200 md:rounded-3xl md:p-4 ${isActive ? "border-cyan-400 bg-cyan-50 shadow-[0_18px_40px_rgba(8,145,178,0.14)]" : unreadCount > 0 ? "border-cyan-200 bg-cyan-50/80 shadow-sm" : "border-transparent bg-slate-50 hover:border-slate-200 hover:bg-white hover:shadow-md md:hover:scale-105"}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="relative">
                            <Avatar name={group.name} src={group.avatar} size="md" />
                            <span className="absolute -bottom-1 -right-1 rounded-full border-2 border-white bg-emerald-500 p-1 text-white shadow-sm">
                              <FiHash className="text-[9px]" />
                            </span>
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <h3 className={`truncate text-sm text-slate-900 ${unreadCount > 0 ? "font-bold" : "font-semibold"}`}>{group.name}</h3>
                                <p className="truncate text-xs text-slate-500">{group.groupType === "department" ? group.departmentName || "Department room" : group.description || "Private working group"}</p>
                              </div>
                              <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 shadow-sm">
                                {group.memberCount}
                              </span>
                            </div>

                            <p className={`mt-2 line-clamp-2 text-sm ${unreadCount > 0 ? "font-semibold text-slate-800" : "text-slate-600"}`}>
                              {latestMessage?.body || group.lastMessagePreview || "No messages yet. Start the conversation."}
                            </p>

                            <div className="mt-3 flex items-center justify-between gap-2 text-xs text-slate-500">
                              <div className="flex -space-x-2">
                                {memberPreview.map((memberName) => (
                                  <div key={memberName} className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-slate-200 text-[10px] font-semibold text-slate-700">
                                    {getInitials(memberName)}
                                  </div>
                                ))}
                                {group.memberCount > memberPreview.length ? (
                                  <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-slate-900 text-[10px] font-semibold text-white">
                                    +{group.memberCount - memberPreview.length}
                                  </div>
                                ) : null}
                              </div>
                              <div className="flex items-center gap-2">
                                <span>{formatTime(group.lastMessageAt) || "New"}</span>
                                {unreadCount > 0 ? <span className="inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-cyan-700 px-1.5 text-[10px] font-bold text-white" aria-label={`${unreadCount} unread messages`}>{unreadCount > 99 ? "99+" : unreadCount}</span> : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex h-full flex-col items-center justify-center px-6 py-16 text-center">
                  <FiMessageCircle className="text-3xl text-slate-300" />
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">No conversations found</h3>
                  <p className="mt-2 max-w-sm text-sm text-slate-500">Try a different search term or create a new group if you have permission.</p>
                </div>
              )}
            </div>
          </aside>

          <main className={`${activeGroup ? "flex" : "hidden lg:flex"} relative h-full min-h-0 flex-col overflow-hidden overscroll-none bg-white md:rounded-[28px] md:border md:border-white/70 md:bg-white/90 md:shadow-[0_24px_80px_rgba(15,23,42,0.10)] md:backdrop-blur-xl`}>
            {activeGroup ? (
              <>
                <header className="border-b border-slate-200 px-3 py-2 lg:px-5 lg:py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-3">
                      <button
                        type="button"
                        onClick={returnToChatList}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-lg text-slate-700 lg:hidden"
                        aria-label="Back to conversations"
                      >
                        &larr;
                      </button>
                      <Avatar name={activeGroup.name} src={activeGroup.avatar} size="md" />
                      <div className="min-w-0 flex flex-wrap items-center gap-1.5">
                        <h2 className="w-full truncate text-base font-semibold text-slate-950 lg:w-auto lg:text-xl">{activeGroup.name}</h2>
                        <span className="hidden rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600 sm:inline-flex">{activeGroup.groupType === "department" ? "Department" : "Custom"}</span>
                        {activeGroup.departmentName ? <span className="hidden rounded-full bg-cyan-50 px-2.5 py-0.5 text-[11px] font-semibold text-cyan-700 sm:inline-flex">{activeGroup.departmentName}</span> : null}
                        <span className="hidden items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-500 sm:inline-flex">
                          <FiUsers />
                          {activeGroup.memberCount}
                        </span>
                        <span className="hidden items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-500 sm:inline-flex">
                          <FiHash />
                          {activeGroup.messageCount}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <button
                        type="button"
                        onClick={openMembersModal}
                        aria-label={`View ${activeGroup.memberCount || 0} group members`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-2 text-xs font-semibold text-slate-700 transition hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-800 sm:text-sm"
                      >
                        <FiUsers />
                        <span className="sm:hidden">{activeGroup.memberCount || 0}</span>
                        <span className="hidden sm:inline">Members</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => loadActiveGroup(activeGroupId)}
                        aria-label="Refresh conversation"
                        className="hidden items-center justify-center rounded-full border border-slate-200 bg-white p-2.5 text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 sm:inline-flex"
                      >
                        <FiRefreshCw className={refreshingGroups || refreshingMessages ? "animate-spin" : ""} />
                      </button>
                    </div>
                  </div>
                </header>

                <div ref={messageListRef} className="min-h-0 flex-1 touch-pan-y overscroll-contain overflow-y-auto bg-[linear-gradient(180deg,_rgba(248,250,252,0.65)_0%,_rgba(255,255,255,0.98)_14%,_rgba(255,255,255,0.98)_100%)] px-3 py-3 pb-40 lg:px-5 lg:py-4 lg:pb-4">
                  {hasMoreMessages ? (
                    <div className="mb-5 flex justify-center">
                      <button
                        type="button"
                        onClick={loadOlderMessages}
                        disabled={loadingOlder}
                        className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {loadingOlder ? "Loading earlier messages..." : "Load earlier messages"}
                      </button>
                    </div>
                  ) : null}

                  {loadingMessages && !messages.length ? (
                    <div className="space-y-4">
                      {[0, 1, 2, 3].map((item) => (
                        <div key={item} className="flex animate-pulse gap-3">
                          <div className="h-12 w-12 rounded-full bg-slate-200" />
                          <div className="flex-1 space-y-3">
                            <div className="h-4 w-40 rounded bg-slate-200" />
                            <div className="h-20 rounded-3xl bg-slate-200" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : messages.length ? (
                    <div className="space-y-6">
                      {Object.entries(groupedMessages).map(([date, dayMessages]) => (
                        <section key={date} className="space-y-4">
                          <div className="flex justify-center">
                            <span className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 shadow-sm">
                              {date}
                            </span>
                          </div>
                          <div className="space-y-4">
                            {dayMessages.map(renderMessage)}
                          </div>
                        </section>
                      ))}
                    </div>
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center px-6 py-20 text-center">
                      <div className="rounded-3xl border border-slate-200 bg-white px-6 py-8 shadow-sm">
                        <FiMessageCircle className="mx-auto text-4xl text-cyan-600" />
                        <h3 className="mt-4 text-xl font-semibold text-slate-950">Start this conversation</h3>
                        <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
                          Send a quick update, mention a teammate with @, or share a request note so the group stays in sync.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <form onSubmit={sendMessage} style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }} className="fixed inset-x-0 bottom-0 z-40 w-full shrink-0 border-t border-slate-200 bg-white/98 px-3 pt-2 shadow-[0_-8px_24px_rgba(15,23,42,0.06)] backdrop-blur lg:relative lg:inset-auto lg:bottom-auto lg:z-30 lg:px-5 lg:pt-2.5">
                  {selectedMentionIds.length ? (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {selectedMentionIds.map((employeeId) => {
                        const employee = selectedMentionLookup.get(employeeId);
                        return (
                          <button
                            key={employeeId}
                            type="button"
                            onClick={() => removeSelectedMention(employeeId)}
                            className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-semibold text-cyan-700 transition hover:border-cyan-300 hover:bg-cyan-100"
                          >
                            @{getEmployeeName(employee)}
                            <FiX />
                          </button>
                        );
                      })}
                    </div>
                  ) : null}

                  {selectedMedia.length ? <div className="mb-2 flex gap-2 overflow-x-auto pb-1">{selectedMedia.map((file, index) => <div key={`${file.name}-${index}`} className="flex max-w-[180px] shrink-0 items-center gap-2 rounded-xl border border-cyan-200 bg-cyan-50 px-2.5 py-2 text-[10px] text-cyan-900 sm:text-xs"><FiPaperclip className="shrink-0"/><span className="truncate">{file.name}</span><button type="button" onClick={() => setSelectedMedia((items) => items.filter((_, itemIndex) => itemIndex !== index))} aria-label={`Remove ${file.name}`}><FiX /></button></div>)}</div> : null}

                  <div className="relative rounded-2xl border border-slate-200 bg-slate-50 p-1.5 shadow-sm md:rounded-[24px] md:p-2.5">
                    <input ref={mediaInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime,audio/mpeg,audio/mp4,audio/ogg,audio/wav,audio/webm,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.doc,.docx" multiple className="sr-only" onChange={selectMedia} />
                    <button type="button" onClick={() => mediaInputRef.current?.click()} disabled={sendingMessage || selectedMedia.length >= 5} aria-label="Attach media or document" className="absolute left-2.5 top-1/2 z-10 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-slate-600 transition hover:bg-cyan-50 hover:text-cyan-700 disabled:opacity-40 md:left-5"><FiPaperclip /></button>
                    <textarea
                      ref={composerRef}
                      value={composerValue}
                      onChange={handleComposerChange}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && event.ctrlKey) {
                          sendMessage(event);
                        }
                      }}
                      placeholder="Message"
                      rows={2}
                      className="h-[48px] w-full max-h-[96px] resize-none rounded-xl border border-transparent bg-white py-3 pl-12 pr-12 text-sm leading-5 outline-none placeholder:text-slate-400 focus:border-cyan-200 focus:ring-4 focus:ring-cyan-100 overflow-y-auto md:h-[72px] md:rounded-[18px] md:py-2.5 md:pl-14 md:pr-14"
                    />

                    <button
                      type="submit"
                      disabled={sendingMessage || (!composerValue.trim() && selectedMedia.length === 0)}
                      aria-label="Send message"
                      className="absolute right-2.5 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-gradient-to-r from-cyan-600 to-slate-900 text-white shadow-lg shadow-cyan-900/20 transition hover:scale-105 disabled:cursor-not-allowed disabled:opacity-60 md:right-5 md:h-10 md:w-10"
                    >
                      <FiSend className="text-base" />
                    </button>

                    {mentionQuery && activeMentionCandidates.length ? (
                      <div className="absolute bottom-[calc(100%+8px)] left-3 right-3 z-20 max-h-[240px] overflow-y-auto rounded-3xl border border-slate-200 bg-white p-2 shadow-2xl shadow-slate-900/15">
                        <div className="mb-2 flex items-center justify-between px-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          <span>Mentions</span>
                          <span>@{mentionQuery}</span>
                        </div>
                        <div className="space-y-1">
                          {activeMentionCandidates.map((employee) => (
                            <button
                              key={employee.id}
                              type="button"
                              onClick={() => insertMention(employee)}
                              className="flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left transition hover:bg-slate-50"
                            >
                              <Avatar name={getEmployeeName(employee)} src={employee.profilePhotoUrl} size="sm" />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-slate-900">{getEmployeeName(employee)}</p>
                                <p className="truncate text-xs text-slate-500">{employee.positionName || employee.departmentName || employee.officialEmail || employee.personalEmail || ""}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}

                  </div>
                </form>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center px-6 py-16 text-center">
                <div className="max-w-lg rounded-[32px] border border-slate-200 bg-white px-8 py-10 shadow-sm">
                  <FiMessageCircle className="mx-auto text-4xl text-cyan-600" />
                  <h3 className="mt-4 text-2xl font-semibold text-slate-950">No conversation selected</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    {loadingGroupDetails ? "Loading the selected room..." : "Pick a group from the left to view messages, manage members, and send updates."}
                  </p>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {showCreateModal ? (
        <ModalShell
          title="Create a group"
          description="Create a focused chat room for a project, request, or cross-functional discussion."
          onClose={() => setShowCreateModal(false)}
          actions={(
            <>
              <button type="button" onClick={() => setShowCreateModal(false)} className="rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">Cancel</button>
              <button type="submit" form="create-group-form" className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800">
                Create group
                <FiPlus />
              </button>
            </>
          )}
        >
          <form id="create-group-form" onSubmit={submitCreateGroup} className="space-y-5">
            {createError ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{createError}</div> : null}
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-2 md:col-span-1">
                <span className="text-sm font-semibold text-slate-700">Group name</span>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(event) => setCreateForm((current) => ({ ...current, name: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100"
                  placeholder="Project Phoenix"
                />
              </label>
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm font-semibold text-slate-700">Description</span>
                <textarea
                  rows={3}
                  value={createForm.description}
                  onChange={(event) => setCreateForm((current) => ({ ...current, description: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100"
                  placeholder="What is this room for?"
                />
              </label>
            </div>

            <div>
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">Add members</span>
                <span className="text-xs text-slate-500">Optional</span>
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                {mentionables.map((employee) => (
                  <MemberPill
                    key={employee.id}
                    employee={employee}
                    selected={createMemberIds.includes(employee.id)}
                    onToggle={toggleCreateMember}
                  />
                ))}
              </div>
            </div>
          </form>
        </ModalShell>
      ) : null}

      {pendingDeleteMessage ? <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/55 p-4" onMouseDown={() => !deletingMessageId && setPendingDeleteMessage(null)}><div role="dialog" aria-modal="true" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onMouseDown={(event) => event.stopPropagation()}><h2 className="text-lg font-bold text-slate-900">Delete message?</h2><p className="mt-2 text-sm leading-6 text-slate-500">This will permanently remove the message and any shared files.</p><div className="mt-6 flex justify-end gap-3"><button type="button" disabled={Boolean(deletingMessageId)} onClick={() => setPendingDeleteMessage(null)} className="rounded-xl border px-4 py-2.5 text-sm font-semibold text-slate-700">Cancel</button><button type="button" disabled={Boolean(deletingMessageId)} onClick={async () => { try { setDeletingMessageId(pendingDeleteMessage.id); await deleteChatMessage(activeGroupId, pendingDeleteMessage.id); setMessages((items) => items.filter((item) => item.id !== pendingDeleteMessage.id)); setPendingDeleteMessage(null); } catch (error) { setError(error?.response?.data?.message || "Unable to delete message."); } finally { setDeletingMessageId(null); } }} className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white">{deletingMessageId ? "Deleting..." : "Delete message"}</button></div></div></div> : null}
      {showMembersModal && activeGroup ? (
        <ModalShell
          title={`Manage members · ${activeGroup.name}`}
          description={`${activeGroup.memberCount || activeGroup.members?.length || 0} group members. Select Message to start or continue a private conversation.`}
          onClose={() => setShowMembersModal(false)}
          actions={(
            <>
              <button type="button" onClick={() => setShowMembersModal(false)} className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 sm:py-2.5 sm:text-sm">Close</button>
              {canManageCurrentGroup ? <button type="submit" form="members-form" disabled={savingMembers} className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 sm:py-2.5 sm:text-sm">
                {savingMembers ? "Saving..." : "Save changes"}<FiUsers />
              </button> : null}
            </>
          )}
        >
          <form id="members-form" onSubmit={submitMemberUpdate} className="space-y-5">
            {memberError ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{memberError}</div> : null}
            <div className="grid gap-2 sm:grid-cols-2">
              {(activeGroup.members || []).map((membership) => {
                const employee = membership.employee || membership;
                const employeeId = membership.employeeId || employee.id;
                const employeeName = getEmployeeName(employee);
                const isCurrentUser = !companyAccount && employeeId === currentEmployee?.id;
                return (
                  <div key={employeeId} className="flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                    <Avatar name={employeeName} src={employee.profilePhotoUrl} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold text-slate-900 sm:text-sm">{employeeName}{isCurrentUser ? " (You)" : ""}</p>
                      <p className="truncate text-[10px] text-slate-500 sm:text-xs">{[employee.positionName, employee.departmentName].filter(Boolean).join(" · ") || employee.officialEmail || employee.personalEmail || membership.role || "Member"}</p>
                    </div>
                    {!isCurrentUser ? <button type="button" onClick={() => openDirectConversation(employeeId)} className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-cyan-50 px-2.5 py-2 text-[10px] font-semibold text-cyan-800 transition hover:bg-cyan-100 sm:px-3 sm:text-xs" aria-label={`Message ${employeeName} directly`}><FiMessageCircle />Message</button> : null}
                  </div>
                );
              })}
            </div>
            {!(activeGroup.members || []).length ? <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500 sm:text-sm">No members are available to display.</div> : null}
            {canManageCurrentGroup ? <div className="border-t border-slate-200 pt-5">
              <div className="mb-3 rounded-2xl bg-slate-50 px-4 py-3 text-[11px] text-slate-600 sm:text-sm">{activeGroup.groupType === "department" ? "Department membership is normally synchronized automatically. Review changes carefully." : "As a group manager, you can add or remove members below."}</div>
              <h3 className="mb-3 text-xs font-semibold text-slate-800 sm:text-sm">Manage membership</h3>
              <div className="grid gap-2 sm:grid-cols-2">{mentionables.map((employee) => <MemberPill key={employee.id} employee={employee} selected={groupMemberIds.includes(employee.id)} onToggle={toggleGroupMember} />)}</div>
            </div> : null}
          </form>
        </ModalShell>
      ) : null}
    </div>
  );
}
